import React, { useState } from 'react';
import { IoAdd, IoSchool, IoCalendar, IoTrophy, IoPencil, IoTrash, IoChevronDown, IoChevronUp } from 'react-icons/io5';
import { School, Course } from '@/services/educationService';
import { useEducationData } from '@/hooks/useEducationData';
import './EducationDataDisplay.css';

interface EducationDataDisplayProps {
  onAddSchool: () => void;
  onEditSchool: (school: School) => void;
}

const EducationDataDisplay: React.FC<EducationDataDisplayProps> = ({
  onAddSchool,
  onEditSchool
}) => {
  const { schools, isLoading, deleteSchool, isDeletingSchool } = useEducationData();
  const [expandedSchools, setExpandedSchools] = useState<Record<number, boolean>>({});

  const toggleSchoolExpansion = (schoolId: number) => {
    setExpandedSchools(prev => ({
      ...prev,
      [schoolId]: !prev[schoolId]
    }));
  };

  const handleDeleteSchool = async (school: School) => {
    if (window.confirm(`Are you sure you want to delete ${school.school_name}? This will also delete all associated courses.`)) {
      try {
        await deleteSchool(school.id);
      } catch (error) {
        console.error('Failed to delete school:', error);
      }
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Not specified';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short' 
    });
  };

  const getGraduationStatus = (school: School) => {
    const statuses = [];
    if (school.graduated_early) statuses.push('Graduated Early');
    if (school.graduated_late) statuses.push('Graduated Late');
    if (school.took_time_off) statuses.push('Took Time Off');
    if (school.took_gap_year) statuses.push('Took Gap Year');
    if (school.changed_schools) statuses.push('Changed Schools');
    return statuses.length > 0 ? statuses.join(', ') : null;
  };

  if (isLoading) {
    return (
      <div className="education-display-loading">
        <div className="education-loading-spinner"></div>
        <p>Loading education data...</p>
      </div>
    );
  }

  if (schools.length === 0) {
    return (
      <div className="education-display-empty">
        <IoSchool className="education-empty-icon" />
        <h3>No Education Information Yet</h3>
        <p>Add your school information to get started with your academic profile.</p>
        <button 
          className="education-add-button primary"
          onClick={onAddSchool}
        >
          <IoAdd className="education-button-icon" />
          Add School
        </button>
      </div>
    );
  }

  return (
    <div className="education-display-container">
      <div className="education-display-header">
        <div className="education-display-title">
          <IoSchool className="education-title-icon" />
          <h3>Education History</h3>
          <span className="education-count">{schools.length} school{schools.length !== 1 ? 's' : ''}</span>
        </div>
        <button 
          className="education-add-button secondary"
          onClick={onAddSchool}
        >
          <IoAdd className="education-button-icon" />
          Add School
        </button>
      </div>

      <div className="education-schools-list">
        {schools.map((school) => (
          <div key={school.id} className="education-school-card">
            <div className="education-school-header">
              <div className="education-school-info">
                <div className="education-school-name-row">
                  <h4 className="education-school-name">{school.school_name}</h4>
                  <div className="education-school-badges">
                    {school.school_type && (
                      <span className="education-badge type">{school.school_type}</span>
                    )}
                    {school.is_boarding_school && (
                      <span className="education-badge boarding">Boarding</span>
                    )}
                  </div>
                </div>
                
                <div className="education-school-details">
                  <div className="education-school-location">
                    {[school.city, school.state, school.country].filter(Boolean).join(', ')}
                  </div>
                  
                  <div className="education-school-dates">
                    <IoCalendar className="education-date-icon" />
                    <span>
                      {formatDate(school.date_of_entry)} - {
                        school.expect_to_graduate ? 
                          (school.date_of_exit ? formatDate(school.date_of_exit) : 'Expected graduation') :
                          formatDate(school.date_of_exit)
                      }
                    </span>
                  </div>

                  {getGraduationStatus(school) && (
                    <div className="education-graduation-status">
                      <span className="education-status-label">Status:</span>
                      <span className="education-status-value">{getGraduationStatus(school)}</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="education-school-actions">
                <button
                  className="education-action-button expand"
                  onClick={() => toggleSchoolExpansion(school.id)}
                  title={expandedSchools[school.id] ? 'Collapse details' : 'Expand details'}
                >
                  {expandedSchools[school.id] ? <IoChevronUp /> : <IoChevronDown />}
                </button>
                <button
                  className="education-action-button edit"
                  onClick={() => onEditSchool(school)}
                  title="Edit school"
                >
                  <IoPencil />
                </button>
                <button
                  className="education-action-button delete"
                  onClick={() => handleDeleteSchool(school)}
                  disabled={isDeletingSchool}
                  title="Delete school"
                >
                  <IoTrash />
                </button>
              </div>
            </div>

            {expandedSchools[school.id] && (
              <div className="education-school-expanded">
                {/* Academic Information */}
                {(school.gpa_value || school.class_rank) && (
                  <div className="education-academic-section">
                    <h5 className="education-section-title">
                      <IoTrophy className="education-section-icon" />
                      Academic Performance
                    </h5>
                    <div className="education-academic-grid">
                      {school.gpa_value && (
                        <div className="education-academic-item">
                          <span className="education-academic-label">GPA:</span>
                          <span className="education-academic-value">
                            {school.gpa_value}{school.gpa_max ? `/${school.gpa_max}` : ''} 
                            {school.gpa_weighted ? ' (Weighted)' : ' (Unweighted)'}
                          </span>
                        </div>
                      )}
                      {school.class_rank && (
                        <div className="education-academic-item">
                          <span className="education-academic-label">Class Rank:</span>
                          <span className="education-academic-value">
                            {school.class_rank} ({school.class_rank_type || 'exact'})
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Courses */}
                {school.courses && school.courses.length > 0 && (
                  <div className="education-courses-section">
                    <h5 className="education-section-title">
                      Recent Courses ({school.courses.length})
                    </h5>
                    <div className="education-courses-grid">
                      {school.courses
                        .sort((a, b) => a.order_index - b.order_index)
                        .map((course) => (
                          <div key={course.id} className="education-course-item">
                            <div className="education-course-name">{course.course_name}</div>
                            <div className="education-course-details">
                              <span className="education-course-subject">
                                {course.subject_override || course.subject}
                              </span>
                              <span className="education-course-level">
                                {course.level_override || course.level}
                              </span>
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>
                )}

                {/* Additional Flags */}
                {(school.received_counselor_help || school.has_academic_honors) && (
                  <div className="education-additional-section">
                    <h5 className="education-section-title">Additional Information</h5>
                    <div className="education-additional-flags">
                      {school.received_counselor_help && (
                        <span className="education-flag">Received counselor help</span>
                      )}
                      {school.has_academic_honors && (
                        <span className="education-flag">Has academic honors</span>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default EducationDataDisplay;